import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { humanize, unescapeHTML, titleize } from 'underscore.string';
import { Link } from 'react-router-dom';
import SessionStore from 'stores/SessionStore';
import moment from 'moment-timezone';
import StateTransition from 'components/v2/state-transition';
import { ProgressCell } from 'components/modules/shipments/renderer';
import Badge from 'components/v2/Badge';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import { sluggify } from 'utils/localization/utils';
import {
  checkShorthaulAppointmentNeeded,
  isShipmentCanceled,
} from 'helpers/shipments';

import { Info, X, Phone, Mail, ChevronDown, ExternalLink } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import ShipmentDetailDateItem from 'components/modules/shipments/details/ShipmentDetailDateItem';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { isLegManagementEnabled } from 'helpers/legs-helper';

import { isProd } from 'helpers/helper_functions';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';

import { getLocationTimezone } from 'helpers/locations';
import { getFirstStop, getLastStop } from 'helpers/stops';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation, Translation, Trans } from 'react-i18next';
import Alert from 'components/ui/Alert';
import BillingStatusRenderer from '../../v2/BillingStatusRenderer';
import { LegNumberSummary } from './LegNumberSummary/LegNumberSummary';

import OpenExceptions from './OpenExceptions';
import DescriptionListItem from '../../v2/DescriptionListItem';
import NextSteps from './next-steps/NextSteps';
import MinimumDocumentRequirements from './MinimumDocumentRequirements';
import { isOrderHazmat } from 'helpers/order-types';

const shipmentTypes = {
  SHORTHAUL: 'shorthaul',
  LONGHAUL: 'longhaul',
  DAYRATE: 'dayrate',
};

const DISABLED_BOL_STATUSES = ['job_canceled', 'job_closed'];

const TOOLTIP_KEYS = {
  expectedProgress: 'EXPECTED_PROGRESS_TOOLTIP',
  actualProgress: 'ACTUAL_PROGRESS_TOOLTIP',
  performance: 'PERFORMANCE_TOOLTIP',
  deleteCarrier: 'CLICK_TO_REMOVE_CARRIER',
};

const getTimezone = (zone) => {
  return moment.tz(zone).zoneAbbr();
};

const formatDate = (date, timezone) => {
  const DATE_FORMAT = 'L LT';
  const formattedDate = moment(date).tz(timezone).format(DATE_FORMAT);
  const formattedTimezone = getTimezone(timezone) || '';
  return `${formattedDate} ${formattedTimezone}`;
};

const getDirectionClassName = (direction) => {
  if (direction === 'up') {
    return 'fa fa-arrow-up';
  }
  return 'fa fa-arrow-down';
};

const getAppointmentLabel = (appointment) => {
  const { date, timezone, appointment_unconfirmed } = appointment;

  if (date && !appointment_unconfirmed) {
    return formatDate(date, timezone);
  }
  return <Translation>{(t) => t('NEEDS_APPOINTMENT')}</Translation>;
};

const StopSummary = ({
  label,
  iconDirection,
  address,
  date,
  value,
  timezone,
  appointment_unconfirmed,
}) => (
  <div data-testid="div-4c77ba51" className="stop__address">
    <p data-testid="p-76340254">
      <strong data-testid="strong-349caf11">{label}</strong>
      {value}
    </p>
    <p data-testid="p-f56ec6d0">
      <i
        data-testid="i-521a0667"
        className={getDirectionClassName(iconDirection)}
      />{' '}
      {address}
    </p>
    <p data-testid="p-6d91f927">
      <small data-testid="small-1110e447">
        <i data-testid="i-0419a98e" className="fa fa-clock-o" />
        {getAppointmentLabel({
          date,
          timezone,
          appointment_unconfirmed,
        })}
      </small>
    </p>
  </div>
);

StopSummary.propTypes = {
  label: PropTypes.string.isRequired,
  iconDirection: PropTypes.oneOf(['up', 'down']),
  address: PropTypes.string.isRequired,
  date: PropTypes.string,
  value: PropTypes.string,
  timezone: PropTypes.string,
  appointment_unconfirmed: PropTypes.bool,
};

function getStopSummaryInfo(stop) {
  const { window_start, window_end, appointment_unconfirmed } = stop;

  const locations = stop.location || [];
  const location = locations[0] || {};
  const timezone = getLocationTimezone(location);

  return {
    location,
    window_start,
    window_end,
    timezone,
    appointment_unconfirmed,
  };
}

function isShipmentWithHazards(shipment) {
  const shipmentHasHazmatDescription =
    shipment.hasOwnProperty('hazmat_description') &&
    shipment.hazmat_description;

  if (isIntermodalShipmentType(shipment)) {
    const order = shipment?.order ?? {};
    return isOrderHazmat(order);
  }

  return shipmentHasHazmatDescription;
}

class StatusBar extends Component {
  static propTypes = {
    shipment: PropTypes.shape({
      shipment_id: PropTypes.string.isRequired,
      shipper_name: PropTypes.string,
      carrier_name: PropTypes.string,
      driver_name: PropTypes.string,
      carrier: PropTypes.shape({
        _id: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      }),
      driver: PropTypes.shape({
        _id: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      }),
      shipper: PropTypes.shape({
        _id: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        sales_rep: PropTypes.string,
      }),
      stops: PropTypes.arrayOf(
        PropTypes.shape({
          location: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              address: PropTypes.string.isRequired,
            }),
          ),
        }),
      ).isRequired,
      on_hold: PropTypes.bool.isRequired,
      pre_arrival: PropTypes.bool,
    }),
    onRemoveCarrier: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      showConfirmToggleExposed: false,
      exposed: false,
      hazmatAlertDismissed: false,
      preNoticeAlertDismissed: false,
      carrierRejectionAlertDismissed: false,
    };
  }

  canManageAccessorials() {
    const shipment = this.props.shipment;
    const isFinanceUser = SessionStore.getUserRoles().indexOf('finance') > -1;

    if (isIntermodalShipmentType(shipment) && !shipment.carrier) {
      return false;
    }
    if (isFinanceUser) {
      return true;
    }
    if (isIntermodalShipmentType(shipment.shipment_type) && !shipment.current) {
      return false;
    }
    if (
      !isIntermodalShipmentType(shipment) &&
      (shipment.invoice || shipment.payout)
    ) {
      return false;
    }

    return true;
  }

  canOpenJob() {
    const shipment = this.props.shipment;

    if (shipment.accepted && shipment.current) return true;
    // The next two are for shorthaul, since invoice and payout (singular) don't exist on drayage shipments.
    if (!shipment.current && shipment.invoice) return false;
    if (!shipment.current && shipment.payout) return false;
    if (shipment.current) return false;

    return true;
  }

  canDriverAssign() {
    const shipment = this.props.shipment;

    const { status, shipment_type, accepted, current, canceled, payout } =
      shipment;
    if (checkShorthaulAppointmentNeeded(shipment_type, status)) return false;
    if (!accepted) return true;
    if (accepted && current) return true;
    if (!current) return false;
    if (!canceled) return false;
    return !payout;
  }

  handleSaveToggleExposed = (exposed) => {
    this.setState({ exposed, showConfirmToggleExposed: true });
  };

  handleConfirmToggleExposed = () => {
    this.setState({ showConfirmToggleExposed: false }, () => {
      this.props.onSaveToggleExposed(this.props.shipment, this.state.exposed);
    });
  };

  handleHideToggleExposed = () =>
    this.setState({ showConfirmToggleExposed: false });

  handleSelection = (key) => () => {
    switch (key) {
      case 'assign':
        this.props.onAssignDriver();
        break;
      case 'email_delivery_order':
        this.props.onSendDOEMailModal();
        break;
      case 'accessorials':
        this.props.onModifyAccessorials();
        break;
      case 'bol':
        this.props.onSendBOLMailModal();
        break;
      case 'rate':
        this.props.onSendRATEEmailModal();
        break;
      case 'create_hold':
        this.props.onCreateHold();
        break;
      case 'open':
        this.props.onReOpenShipment();
        break;
      case 'cancel':
        this.props.onShowCancelShipmentModal();
        break;
      case 'clone':
        this.props.onCloneShipment();
        break;
      case 'bulk_accessorials':
        this.props.onBulkAddAccessorial();
        break;
      case 'generate_carrier_204_tendered':
        this.props.onGenerateCarrier204('load_tendered');
        break;
      case 'generate_carrier_204_updated':
        this.props.onGenerateCarrier204('load_updated');
        break;
      case 'generate_carrier_204_canceled':
        this.props.onGenerateCarrier204('load_canceled');
        break;
      case 'overrideBillingStatus':
        this.props.onOverrideBillingStatus();
        break;
      case 'markBillingStatusPendingReview':
        this.props.onMarkBillingStatusPendingReview();
        break;
      default:
    }
  };

  getStatus() {
    const { t } = this.props;
    const status = this.props.shipment.status;

    switch (status) {
      case 'job_closed':
        return t('CLOSED');
      case 'job_canceled':
        return t('CANCELED');
      case 'needs_appt':
        return t('NEEDS_APPT');
      default:
    }

    return t(sluggify(status));
  }

  getQuickPayTag() {
    const { t } = this.props;
    const quick_payment_contract = this.props.shipment?.quick_payment_contract;
    if (quick_payment_contract)
      return (
        <Badge className="ml-2" color="blue">
          {t('QUICK_PAY')}
        </Badge>
      );
  }

  canClone() {
    const { shipment } = this.props;
    if (isIntermodalShipmentType(shipment) || shipment.order) {
      return false;
    }
    if (shipment && shipment.shipper && shipment.shipper.tags) {
      const tags = shipment.shipper.tags;

      if (tags.indexOf('block_shipment_clones') > -1) {
        return false;
      }
    }

    return true;
  }

  renderCarrierPopoverContent() {
    const { shipment } = this.props;
    return (
      <div className="space-y-1 text-sm">
        <p className="font-semibold">
          {unescapeHTML(this.props.shipment.carrier_name)}
        </p>
        <p className="flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          {shipment.carrier.phone}
        </p>
      </div>
    );
  }

  renderCarrier() {
    if (!this.props.shipment.carrier) {
      return null;
    }
    const {
      t,
      shipment: { carrier },
    } = this.props;
    const isPortProCarrier = 'portpro' in carrier && carrier?.portpro?.enabled;

    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-7132f3fd"
        label={titleize(t('CARRIER', { count: 1 }))}
        className=""
      >
        <div data-testid="div-5a585ab5" className="flex items-center space-x-2">
          <div
            data-testid="div-525c15e5"
            className="flex items-center space-x-1"
          >
            <div data-testid="div-c42e88ca" className="relative">
              <Link to={`/users/${carrier._id}`}>
                <strong data-testid="strong-66dfa6de">
                  {unescapeHTML(this.props.shipment.carrier_name)}
                </strong>
              </Link>
              {carrier.mvp ? (
                <img
                  data-testid="img-d7503e76"
                  src="/img/cargomatic-circle.png"
                  className="absolute -top-5 -right-2 w-3"
                  alt="MVP"
                />
              ) : null}
              {isPortProCarrier ? (
                <img
                  data-testid="img-824e88df"
                  src="/img/icons/portProLogo.png"
                  className="absolute -top-6 -right-11"
                  title={t('CARRIER_INTEGRATED_WITH_PORTPRO')}
                  alt={t('CARRIER_INTEGRATED_WITH_PORTPRO')}
                />
              ) : null}
            </div>
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <button data-testid="div-be76024e" className="inline-flex">
                    <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {this.renderCarrierPopoverContent()}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </div>
          {this.renderRemoveCarrierButton()}
        </div>
      </DescriptionListItem>
    );
  }

  renderShipperPopoverContent() {
    const {
      shipment: { shipper },
    } = this.props;
    return (
      <div className="space-y-1 text-sm">
        <p className="font-semibold">{unescapeHTML(shipper.company_name)}</p>
        <p>
          <Trans
            i18nKey="SALES_REP_WITH_NAME"
            values={{ sales_rep: shipper.sales_rep }}
            components={[<strong key="sales-rep-strong" className="mr-1" />]}
          />
        </p>
        <p className="flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          {shipper.phone}
        </p>
        <p className="flex items-center gap-1.5">
          <Mail className="w-3 h-3" />
          {shipper.email}
        </p>
      </div>
    );
  }

  renderShipper() {
    const { t } = this.props;
    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-e8764ae4"
        label={titleize(t('SHIPPER', { count: 1 }))}
        className=""
      >
        <div data-testid="div-a66d80f4" className="flex items-center space-x-2">
          <div
            data-testid="div-6b5bd0f2"
            className="flex items-center space-x-1"
          >
            <Link to={`/users/${this.props.shipment.shipper._id}`}>
              <strong data-testid="strong-22102e64">
                {unescapeHTML(this.props.shipment.shipper_name)}
              </strong>
            </Link>
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <button data-testid="div-3aa4d126" className="inline-flex">
                    <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {this.renderShipperPopoverContent()}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </div>
        </div>
      </DescriptionListItem>
    );
  }

  renderConsigneePopoverContent() {
    const {
      shipment: { consignee },
    } = this.props;
    return (
      <div className="space-y-1 text-sm">
        <p className="font-semibold">{unescapeHTML(consignee?.company_name)}</p>
        <p className="flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          {consignee?.phone}
        </p>
        <p className="flex items-center gap-1.5">
          <Mail className="w-3 h-3" />
          {consignee?.email}
        </p>
      </div>
    );
  }

  renderConsignee() {
    if (!this.props.shipment?.consignee) {
      return null;
    }
    const { t } = this.props;
    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-df4119c2"
        label={titleize(t('CONSIGNEE', { count: 1 }))}
        className=""
      >
        <div data-testid="div-2e11febc" className="flex items-center space-x-2">
          <div
            data-testid="div-548e9d1f"
            className="flex items-center space-x-1"
          >
            <Link to={`/users/${this.props?.shipment?.consignee?._id}`}>
              <strong data-testid="strong-2c0785c7">
                {unescapeHTML(this.props?.shipment?.consignee_name)}
              </strong>
            </Link>
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <button data-testid="div-4f41697d" className="inline-flex">
                    <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {this.renderConsigneePopoverContent()}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </div>
        </div>
      </DescriptionListItem>
    );
  }

  renderDriverPopoverContent() {
    const { shipment } = this.props;
    return (
      <div className="space-y-1 text-sm">
        <p className="font-semibold">{unescapeHTML(shipment.driver_name)}</p>
        <p className="flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          {shipment.driver.phone}
        </p>
      </div>
    );
  }

  renderRemoveCarrierButton = () => {
    if (!isIntermodalShipmentType(this.props.shipment)) return null;
    const { t } = this.props;

    return (
      <TooltipProvider>
        <ShadcnTooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="button-27e2e87a"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted"
              onClick={this.props.onRemoveCarrier}
              aria-label="remove button"
            >
              <X className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {t(TOOLTIP_KEYS.deleteCarrier)}
          </TooltipContent>
        </ShadcnTooltip>
      </TooltipProvider>
    );
  };

  renderDriver() {
    if (!this.props.shipment.driver) return null;
    const { t } = this.props;
    return (
      <DescriptionListItem
        dataTestId="DescriptionListItem-8043a6a8"
        className=""
        label={titleize(t('DRIVER', { count: 1 }))}
      >
        <div data-testid="div-cf245287" className="flex items-center space-x-2">
          <div
            data-testid="div-863fdc99"
            className="flex items-center space-x-1"
          >
            <Link to={`/users/${this.props.shipment.driver._id}`}>
              <strong data-testid="strong-42e5d6f6">
                {unescapeHTML(this.props.shipment.driver_name)}
              </strong>
            </Link>
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <button data-testid="div-e5a952d5" className="inline-flex">
                    <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  {this.renderDriverPopoverContent()}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </div>
          {this.renderRemoveCarrierButton()}
        </div>
      </DescriptionListItem>
    );
  }

  renderReviewDate() {
    const { t, shipment } = this.props;
    return (
      <ShipmentDetailDateItem
        dataTestId="shipmentStatusBarReviewDate"
        className="mt-2 lg:col-span-2 z-[45]"
        titleClassName="!mb-0 text-sm font-medium text-gray-500"
        title={t('REVIEW_DATE')}
        key="Review Date"
        value={shipment.review_date}
        editable
        onSave={(event, value) => {
          ShipmentsActionCreators.updateImportantDates(shipment, event, value);
        }}
        param="review_date"
        useNamedUpdate
      />
    );
  }

  renderFirstStop() {
    const { t } = this.props;
    const firstStop = getFirstStop(this.props.shipment.stops);
    if (!firstStop) {
      return null;
    }

    const stopInfo = getStopSummaryInfo(firstStop);
    return (
      <StopSummary
        label={`${t('STARTING_ORIGIN')} `}
        value={humanize(unescapeHTML(stopInfo.location.name))}
        address={stopInfo.location.address}
        iconDirection="up"
        date={stopInfo.window_end}
        timezone={stopInfo.timezone}
        appointment_unconfirmed={stopInfo.appointment_unconfirmed}
      />
    );
  }

  renderLastStop() {
    const { t } = this.props;
    const lastStop = getLastStop(this.props.shipment.stops);
    if (!lastStop) {
      return null;
    }

    const stopInfo = getStopSummaryInfo(lastStop);
    return (
      <StopSummary
        label={`${t('FINISING_DESTINATION')} `}
        value={humanize(unescapeHTML(stopInfo.location.name))}
        address={stopInfo.location.address}
        iconDirection="down"
        date={stopInfo.window_end}
        timezone={stopInfo.timezone}
        appointment_unconfirmed={stopInfo.appointment_unconfirmed}
      />
    );
  }

  transition = (status) => {
    this.props.onShipmentTransition(this.props.shipment, status, {});
  };

  renderStateTransitions() {
    return (
      <div data-testid="div-aa55e577" className="">
        <StateTransition
          dataTestId="StateTransition-9cefc01b"
          progress={this.props.progress ? this.props.progress : null}
          regress={this.props.regress ? this.props.regress : null}
          onClick={this.transition}
        />
      </div>
    );
  }

  renderProgress() {
    const {
      shipment: {
        expected_progress_percentage,
        actual_progress_percentage,
        total_expected_progress_percentage,
        performance_percentage,
      },
      t,
    } = this.props;
    return (
      <div data-testid="div-dfd7845f">
        <strong data-testid="strong-f9c8c16e">{t('EXPECTED_PROGRESS')} </strong>
        <TooltipProvider>
          <ShadcnTooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Info className="w-4 h-4 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t(TOOLTIP_KEYS.expectedProgress)}</TooltipContent>
          </ShadcnTooltip>
        </TooltipProvider>
        <ProgressCell
          actual={expected_progress_percentage}
          expected={total_expected_progress_percentage}
        />
        <strong data-testid="strong-67624d13">{t('ACTUAL_PROGRESS')} </strong>
        <TooltipProvider>
          <ShadcnTooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Info className="w-4 h-4 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t(TOOLTIP_KEYS.actualProgress)}</TooltipContent>
          </ShadcnTooltip>
        </TooltipProvider>
        <ProgressCell
          actual={actual_progress_percentage}
          expected={total_expected_progress_percentage}
        />
        <strong data-testid="strong-450a928d">{t('PERFORMANCE')} </strong>
        <TooltipProvider>
          <ShadcnTooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Info className="w-4 h-4 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t(TOOLTIP_KEYS.performance)}</TooltipContent>
          </ShadcnTooltip>
        </TooltipProvider>
        <ProgressCell actual={performance_percentage} expected={100} />
      </div>
    );
  }
  renderActionsMenu() {
    let { shipment, t } = this.props;
    const { status, shipment_type } = shipment;
    const isLegacyAccounting = !isLegManagementEnabled(
      this.props?.shipment?.order,
    );

    const shouldDisableBOL =
      DISABLED_BOL_STATUSES.includes(status) || isShipmentWithHazards(shipment);
    const shouldShow =
      shipment_type === shipmentTypes.SHORTHAUL ||
      shipment_type === shipmentTypes.LONGHAUL ||
      shipment_type === shipmentTypes.DAYRATE;
    const shouldShowRATE = !shouldShow;
    const shouldDisableAccessorial =
      isIntermodalShipmentType(shipment) && status === 'job_canceled'
        ? isShipmentCanceled(shipment)
        : !this.canManageAccessorials();

    return (
      <ShadcnDropdownMenu data-testid="DropdownMenu-8e367603">
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid="DropdownMenu-8e367603">
            {t('ACTIONS')}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            disabled={!this.canDriverAssign()}
            onSelect={this.handleSelection('assign')}
          >
            {t('ASSIGN')}
          </DropdownMenuItem>
          {isIntermodalShipmentType(this.props.shipment) && (
            <DropdownMenuItem
              onSelect={this.handleSelection('email_delivery_order')}
            >
              {t('EMAIL_DELIVERY_ORDER')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={!this.canOpenJob()}
            onSelect={this.handleSelection('open')}
          >
            {t('REOPEN')}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={shouldDisableBOL}
            onSelect={this.handleSelection('bol')}
          >
            {t('GENERATE_BOL')}
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a
              href={`/tracking?id=${this.props.shipment.shipment_id}`}
              target="_blank"
              rel="noreferrer"
            >
              {t('TRACKING_LINK')}
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={this.handleSelection('create_hold')}>
            {t('CREATE_HOLD')}
          </DropdownMenuItem>
          {!shouldShowRATE && (
            <DropdownMenuItem onSelect={this.handleSelection('rate')}>
              {t('GENERATE_RATE_CONFIRMATION')}
            </DropdownMenuItem>
          )}
          {!isProd() && (
            <DropdownMenuItem
              onSelect={this.handleSelection('generate_carrier_204_tendered')}
            >
              {t('SEND_EDI204_TENDERED')}
            </DropdownMenuItem>
          )}
          {!isProd() && (
            <DropdownMenuItem
              onSelect={this.handleSelection('generate_carrier_204_updated')}
            >
              {t('SEND_EDI204_UPDATED')}
            </DropdownMenuItem>
          )}
          {!isProd() && (
            <DropdownMenuItem
              onSelect={this.handleSelection('generate_carrier_204_canceled')}
            >
              {t('SEND_EDI204_CANCELED')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={shouldDisableAccessorial}
            onSelect={this.handleSelection('accessorials')}
          >
            {t('ACCESSORIAL', { count: 3 })}
          </DropdownMenuItem>
          {isIntermodalShipmentType(this.props.shipment) && (
            <DropdownMenuItem
              disabled={shouldDisableAccessorial}
              onSelect={this.handleSelection('bulk_accessorials')}
            >
              {t('BULK_ACCESSORIALS')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={!this.canClone()}
            onSelect={this.handleSelection('clone')}
          >
            {t('CLONE')}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!this.props.shipment.current}
            onSelect={this.handleSelection('cancel')}
          >
            {t('CANCEL')}
          </DropdownMenuItem>
          {SessionStore.isBillingStatusAdmin() && isLegacyAccounting && (
            <DropdownMenuItem
              onSelect={this.handleSelection('overrideBillingStatus')}
            >
              {t('OVERRIDE_BILLING_STATUS')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </ShadcnDropdownMenu>
    );
  }

  renderHazmatAlert() {
    const { t, shipment } = this.props;
    const order = shipment?.order ?? {};
    const hasHazmat = isOrderHazmat(order);

    if (!hasHazmat || this.state.hazmatAlertDismissed) {
      return null;
    }

    return (
      <Alert
        dataTestId="Alert-05b61aey"
        type="warning"
        className="mb-4"
        title={t('HAZMAT_WARNING_HEADER')}
        canClose
        onClose={() => this.setState({ hazmatAlertDismissed: true })}
      >
        <p data-testid="p-7ddcba2f">{t('HAZMAT_WARNING_BODY')}</p>
      </Alert>
    );
  }

  renderPreNoticeAlert() {
    const { t, shipment } = this.props;
    const { pre_arrival = false } = shipment || {};

    if (
      !pre_arrival ||
      !featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL) ||
      this.state.preNoticeAlertDismissed
    ) {
      return null;
    }

    return (
      <Alert
        dataTestId="Alert-07bf1a4d"
        type="error"
        className="mb-4"
        title={t('PRE_NOTICE_SHIPMENT_HEADER')}
        canClose
        onClose={() => this.setState({ preNoticeAlertDismissed: true })}
      >
        <p data-testid="p-7ddcba2f">{t('PRE_NOTICE_SHIPMENT_BODY')}</p>
      </Alert>
    );
  }

  renderCarrierRejectionAlert() {
    const { t, shipment } = this.props;

    const hasCarrierRejectedShipment =
      shipment.carrier?.portpro?.rejected?.includes(shipment._id);

    if (
      !hasCarrierRejectedShipment ||
      this.state.carrierRejectionAlertDismissed
    ) {
      return null;
    }

    return (
      <Alert
        dataTestId="Alert-8852c662"
        type="warning"
        className="mb-4"
        title={t('REJECTION_WARNING_HEADER')}
        canClose
        onClose={() => this.setState({ carrierRejectionAlertDismissed: true })}
      >
        <p data-testid="p-ea0c6bf1">{t('REJECTION_WARNING_BODY')}</p>
      </Alert>
    );
  }

  render() {
    const {
      shipment: {
        status,
        stops,
        shipment_id,
        shipment_type,
        payables,
        on_hold,
        carrier,
      },
      onNextActionsDateClick,
      t,
    } = this.props;

    const isDrayageOrRailShipment =
      shipment_type &&
      (shipment_type === 'drayage' || shipment_type === 'rail');

    let exposeHidden = true;

    const carrierCost = payables
      .filter((c) => c.message && c.message.includes('Carrier Cost'))
      .reduce((sum, c) => sum + (c.value || 0), 0);

    const hasAutoExposeDrayageShipmentTag = (
      this.props.shipment?.shipper?.tags || []
    ).includes('auto_expose_drayage_shipments');

    if (isDrayageOrRailShipment && !carrier) {
      exposeHidden = false;
    }

    if (!stops) {
      return null;
    }

    return (
      <Card data-testid="div-f81b29f7" className="mb-4">
        <CardContent className="relative pt-6 lg:grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          <div data-testid="div-a00cfcb4" className="lg:col-span-12">
            {this.renderPreNoticeAlert()}
            {this.renderHazmatAlert()}
            {this.renderCarrierRejectionAlert()}
            <OpenExceptions shipment={this.props.shipment} />
            <div
              data-testid="div-2f90417a"
              className="flex items-center justify-between flex-wrap"
            >
              <div
                data-testid="div-0d8b52c9"
                className="flex items-center flex-wrap gap-8"
              >
                <div className="flex items-center gap-2">
                  <h3 data-testid="h3-4e21e032" className="my-0">
                    {shipment_id}
                  </h3>
                  <ShipmentStatusBadge
                    status={status}
                    label={this.getStatus()}
                  />

                  {!isLegManagementEnabled(this.props?.shipment?.order) && (
                    <BillingStatusRenderer {...this.props} />
                  )}

                  {on_hold && (
                    <Badge dataTestId="Badge-a8f30095" className="" color="red">
                      {t('ON_HOLD')}
                    </Badge>
                  )}

                  {this.getQuickPayTag()}
                </div>

                {isIntermodalShipmentType(this.props.shipment) &&
                  isLegManagementEnabled(this.props.shipment?.order) && (
                    <LegNumberSummary id={this.props?.shipment?._id} />
                  )}
              </div>
              <div
                data-testid="div-c4cdb935"
                className="flex flex-wrap items-center my-4 lg:my-0 gap-4"
              >
                {!exposeHidden && (
                  <div data-testid="div-881f5657" className="flex items-center">
                    <label
                      data-testid="label-8b29d2bf"
                      className="mb-0 mr-2 form-label"
                    >
                      {t('EXPOSE')}
                    </label>
                    <Switch
                      checked={this.props.shipment.exposed}
                      onCheckedChange={this.handleSaveToggleExposed}
                    />
                  </div>
                )}
                {this.renderStateTransitions()}
                <div data-testid="div-82424d60" className="">
                  {this.renderActionsMenu()}
                </div>
                {this.props.showCloseButton && this.props.onClose && (
                  <div
                    data-testid="div-ccbd4672"
                    className="absolute top-0 right-0"
                  >
                    <Button
                      size="icon"
                      variant="icon"
                      data-testid="button-7904e57e"
                      className="w-10 h-10"
                      onClick={() => this.props.onClose()}
                      aria-label="close button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div data-testid="div-56f513c8" className="lg:col-span-4">
            <div
              data-testid="div-8cadbdeb"
              className="lg:grid lg:grid-cols-2 lg:gap-2"
            >
              {this.props.shipment?.order?._id && (
                <DescriptionListItem label={t('ORDER')}>
                  <Link
                    to={`/orders/${this.props.shipment.order._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <strong>
                      {this.props.shipment.order.order_id ||
                        this.props.shipment.order._id}
                    </strong>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </DescriptionListItem>
              )}
              {this.renderShipper()}
              {this.renderCarrier()}
              {this.renderDriver()}
              {this.renderConsignee()}
              {this.renderReviewDate()}
            </div>
          </div>
          {featureEnabled(CONFIGURED_FEATURES.SSR) &&
          isIntermodalShipmentType(this.props.shipment) ? (
            <NextSteps
              onNextActionsDateClick={(item) => onNextActionsDateClick(item)}
              shipment={this.props.shipment}
            />
          ) : (
            <>
              <div data-testid="div-d6592829" className="lg:col-span-3">
                {this.renderFirstStop()}
              </div>
              <div data-testid="div-8548b554" className="lg:col-span-3">
                {this.renderLastStop()}
              </div>
              <div data-testid="div-9ff9482f" className="lg:col-span-2">
                {this.renderProgress()}
              </div>
            </>
          )}
          {featureEnabled(CONFIGURED_FEATURES.SSR) &&
            isIntermodalShipmentType(this.props.shipment) && (
              <MinimumDocumentRequirements shipment={this.props.shipment} />
            )}
          {this.state.showConfirmToggleExposed && (
            <ConfirmModal
              dataTestId="ConfirmModal-b5242d33"
              title={
                this.state.exposed
                  ? t('EXPOSE_SHIPMENT')
                  : t('UNEXPOSE_SHIPMENT')
              }
              message={
                !hasAutoExposeDrayageShipmentTag && carrierCost <= 0
                  ? t('WARNING_CARRIER_COST_ARE_YOU_SURE')
                  : t('ARE_YOU_SURE')
              }
              show={this.state.showConfirmToggleExposed}
              onConfirm={this.handleConfirmToggleExposed}
              onHide={this.handleHideToggleExposed}
            />
          )}
        </CardContent>
      </Card>
    );
  }
}

export default withTranslation()(StatusBar);
